import {
  CheckCircle2,
  ChevronDown,
  LoaderCircle,
  PlugZap,
  Save,
  Search,
  ShieldCheck,
  Zap,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";

import { api } from "@/api";
import { AdminToolsSection } from "@/components/AdminToolsSection";
import { StatusBadge } from "@/components/StatusBadge";
import { ThemeModePicker } from "@/components/ThemeModePicker";

function errorMessage(error, fallback) {
  const contentType = error.response?.headers?.["content-type"] || "";
  const detail = error.response?.data?.detail;
  return contentType.includes("application/json") && detail ? detail : fallback;
}

export default function NoCRMSettingsPage() {
  const [settings, setSettings] = useState(null);
  const [apiKey, setApiKey] = useState("");
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [loadingPipelines, setLoadingPipelines] = useState(false);
  const [loadingActivities, setLoadingActivities] = useState(false);
  const [activatingAutomation, setActivatingAutomation] = useState(false);
  const [verifyingWebhook, setVerifyingWebhook] = useState(false);
  const [catalogue, setCatalogue] = useState(null);
  const [activityCatalogue, setActivityCatalogue] = useState(null);
  const [pipelineSearch, setPipelineSearch] = useState("");

  useEffect(() => {
    api.get("/settings").then(({ data }) => setSettings(data));
  }, []);

  const filteredPipelines = useMemo(() => {
    const query = pipelineSearch.trim().toLocaleLowerCase("fr-FR");
    return (catalogue?.pipelines || []).filter((pipeline) => !query || pipeline.name.toLocaleLowerCase("fr-FR").includes(query));
  }, [catalogue, pipelineSearch]);

  const activeSteps = useMemo(() => {
    if (!settings?.nocrm.pipeline_id) return [];
    return (catalogue?.steps || []).filter((step) => (step.pipeline_id ?? step.pipeline?.id) === Number(settings.nocrm.pipeline_id));
  }, [catalogue, settings?.nocrm.pipeline_id]);
  const triggerStep = activeSteps.find((step) => step.name === "Dossiers à enrichir");

  if (!settings) {
    return <section className="page" data-testid="settings-loading"><p data-testid="settings-loading-message">Chargement des paramètres…</p></section>;
  }

  const updateNocrm = (key, value) => {
    setSettings((current) => ({
      ...current,
      nocrm: { ...current.nocrm, [key]: value },
    }));
  };

  const saveSettings = async (silent = false) => {
    setSaving(true);
    try {
      const { data } = await api.put("/settings", {
        business: settings.business,
        nocrm: { ...settings.nocrm, api_key: apiKey || null },
      });
      setSettings(data);
      setApiKey("");
      if (!silent) toast.success("Paramètres enregistrés.");
      return data;
    } catch (error) {
      if (!silent) toast.error("Les paramètres n’ont pas été enregistrés. Vérifiez les champs indiqués.");
      throw error;
    } finally {
      setSaving(false);
    }
  };

  const testConnection = async () => {
    setTesting(true);
    try {
      await saveSettings(true);
      const { data } = await api.post("/settings/nocrm/test");
      setSettings((current) => ({ ...current, nocrm: { ...current.nocrm, connection_status: "connectee", automation_paused: false } }));
      toast.success(data.message);
    } catch (error) {
      toast.error(errorMessage(error, "Connexion impossible. Les automatisations restent en pause."));
    } finally {
      setTesting(false);
    }
  };

  const loadPipelines = async () => {
    setLoadingPipelines(true);
    try {
      const { data } = await api.get("/settings/nocrm/steps");
      const preferredId = settings.nocrm.pipeline_id || data.suggested_pipeline_id;
      const preferred = data.pipelines.find((pipeline) => pipeline.id === preferredId);
      setCatalogue(data);
      if (preferredId) {
        setSettings((current) => ({
          ...current,
          nocrm: { ...current.nocrm, pipeline_id: preferredId },
        }));
        setPipelineSearch("");
      }
      toast.success(preferred ? `Pipeline « ${preferred.name} » sélectionné.` : "Pipelines noCRM chargés.");
    } catch (error) {
      toast.error(errorMessage(error, "Impossible de charger les pipelines noCRM."));
    } finally {
      setLoadingPipelines(false);
    }
  };

  const choosePipeline = (event) => {
    const pipelineId = Number(event.target.value) || null;
    setSettings((current) => ({
      ...current,
      nocrm: {
        ...current.nocrm,
        pipeline_id: pipelineId,
        success_step_id: null,
        review_step_id: null,
      },
    }));
  };

  const loadActivities = async () => {
    setLoadingActivities(true);
    try {
      const { data } = await api.get("/settings/nocrm/activities");
      setActivityCatalogue(data);
      if (data.suggested_activity_id) {
        const updatedSettings = {
          ...settings,
          nocrm: { ...settings.nocrm, activity_id: data.suggested_activity_id },
        };
        const { data: savedSettings } = await api.put("/settings", {
          business: updatedSettings.business,
          nocrm: { ...updatedSettings.nocrm, api_key: null },
        });
        setSettings(savedSettings);
      }
      toast.success(data.suggested_activity_id ? "L’activité « Enrichissement automatique » a été détectée." : "Activités noCRM chargées.");
    } catch (error) {
      toast.error(errorMessage(error, "Impossible de charger les activités noCRM."));
    } finally {
      setLoadingActivities(false);
    }
  };

  const activateAutomation = async () => {
    setActivatingAutomation(true);
    try {
      const { data } = await api.post("/settings/nocrm/automation/activate", null, {
        params: { callback_base_url: process.env.REACT_APP_BACKEND_URL },
      });
      setSettings((current) => ({
        ...current,
        nocrm: { ...current.nocrm, automation_enabled: true },
      }));
      toast.success(data.message);
    } catch (error) {
      toast.error(errorMessage(error, "L’automatisation n’a pas pu être activée. Vérifiez les étapes noCRM puis réessayez."));
    } finally {
      setActivatingAutomation(false);
    }
  };

  const verifyWebhook = async () => {
    setVerifyingWebhook(true);
    try {
      const { data } = await api.post("/settings/nocrm/webhook/verify");
      if (data.status === "repaired") {
        toast.success(data.message);
      } else if (data.status === "ok") {
        toast.success(data.message);
      } else {
        toast.info(data.message);
      }
    } catch (error) {
      toast.error(errorMessage(error, "La vérification du webhook a échoué."));
    } finally {
      setVerifyingWebhook(false);
    }
  };

  return (
    <section className="page" data-testid="settings-page">
      <header className="page-heading">
        <div>
          <p className="eyebrow">CONFIGURATION</p>
          <h1 data-testid="settings-page-title">Réglages<br />de l'espace</h1>
          <p className="page-subtitle">Tous les réglages métier et noCRM sont gérés ici.</p>
        </div>
        <button className="primary-button" type="button" onClick={() => saveSettings()} disabled={saving || testing} data-testid="save-settings-button">
          {saving ? <LoaderCircle className="spinning" size={18} /> : <Save size={18} />}
          Enregistrer
        </button>
      </header>

      <div className="settings-layout crm-settings">
        <aside className="card crm-settings__side" data-testid="settings-summary-card">
          <span className="crm-settings__ico"><PlugZap size={30} /></span>
          <h2>noCRM</h2>
          <p className="crm-settings__sub">{settings.nocrm.subdomain ? `${settings.nocrm.subdomain}.nocrm.io` : "Sous-domaine à renseigner"}</p>
          <StatusBadge status={settings.nocrm.connection_status} />
          <dl className="crm-settings__facts">
            <div><dt>Automatisation</dt><dd data-testid="settings-summary-automation">{settings.nocrm.automation_enabled ? "Active" : settings.nocrm.automation_paused ? "En pause" : "Inactive"}</dd></div>
            <div><dt>Pipeline mairies</dt><dd>{settings.nocrm.pipeline_id ? "Choisi" : "À choisir"}</dd></div>
          </dl>
          <ThemeModePicker />
        </aside>
        <section className="settings-section card" data-testid="nocrm-settings-section">
          <div className="section-title">
            <PlugZap size={20} />
            <div><p className="eyebrow">INTÉGRATION</p><h2>Connexion noCRM</h2></div>
            <StatusBadge status={settings.nocrm.connection_status} />
          </div>
          <label>Sous-domaine noCRM<input value={settings.nocrm.subdomain || ""} onChange={(event) => updateNocrm("subdomain", event.target.value)} data-testid="nocrm-subdomain-input" placeholder="votre-compte" /></label>
          <label>Clé API noCRM<input type="password" value={apiKey} onChange={(event) => setApiKey(event.target.value)} data-testid="nocrm-api-key-input" placeholder={settings.nocrm.configured ? "Clé enregistrée — laissez vide pour conserver" : "Collez votre clé ici"} /></label>
          <div className="button-row">
            <button className="secondary-button" type="button" onClick={testConnection} disabled={testing || saving} data-testid="test-nocrm-connection-button">
              {testing || saving ? <LoaderCircle className="spinning" size={17} /> : <CheckCircle2 size={17} />}
              Tester et enregistrer la connexion
            </button>
            <button className="secondary-button" type="button" onClick={loadPipelines} disabled={loadingPipelines} data-testid="load-nocrm-pipelines-button">
              {loadingPipelines ? <LoaderCircle className="spinning" size={17} /> : <ChevronDown size={17} />}
              Charger les pipelines
            </button>
            <button className="secondary-button" type="button" onClick={loadActivities} disabled={loadingActivities} data-testid="load-nocrm-activities-button">
              {loadingActivities ? <LoaderCircle className="spinning" size={17} /> : <CheckCircle2 size={17} />}
              Charger les activités
            </button>
            <button className="secondary-button" type="button" onClick={verifyWebhook} disabled={verifyingWebhook} data-testid="verify-webhook-button">
              {verifyingWebhook ? <LoaderCircle className="spinning" size={17} /> : <ShieldCheck size={17} />}
              Vérifier & réparer le webhook
            </button>
          </div>
          {settings.nocrm.automation_paused && <p className="review-alert" data-testid="automation-paused-message">Les automatisations sont en pause jusqu’à ce que la connexion noCRM soit validée.</p>}

          {catalogue && <div className="pipeline-controls" data-testid="pipeline-selector-panel">
            <label>Rechercher un pipeline<input value={pipelineSearch} onChange={(event) => setPipelineSearch(event.target.value)} data-testid="pipeline-search-input" placeholder="Ex. Climatiseurs EDF" /></label>
            <label>Pipeline des leads mairies<select value={settings.nocrm.pipeline_id || ""} onChange={choosePipeline} data-testid="nocrm-pipeline-select"><option value="">Sélectionnez un pipeline</option>{filteredPipelines.map((pipeline) => <option key={pipeline.id} value={pipeline.id}>{pipeline.name}</option>)}</select></label>
            {settings.nocrm.pipeline_id && <p className="pipeline-count" data-testid="selected-pipeline-step-count">{activeSteps.length} étape(s) dans ce pipeline.</p>}
            {activeSteps.length > 0 && <div className="form-grid">
              <label>Étape déclencheur enrichissement<select value={settings.nocrm.trigger_step_id || triggerStep?.id || ""} onChange={(event) => updateNocrm("trigger_step_id", Number(event.target.value) || null)} data-testid="trigger-step-select"><option value="">Choisir une étape</option>{activeSteps.map((step) => <option key={step.id} value={step.id}>{step.name}</option>)}</select></label>
              <label>Étape après enrichissement<select value={settings.nocrm.success_step_id || ""} onChange={(event) => updateNocrm("success_step_id", Number(event.target.value) || null)} data-testid="success-step-select"><option value="">Aucun déplacement</option>{activeSteps.map((step) => <option key={step.id} value={step.id}>{step.name}</option>)}</select></label>
              <label>Étape à vérifier<select value={settings.nocrm.review_step_id || ""} onChange={(event) => updateNocrm("review_step_id", Number(event.target.value) || null)} data-testid="review-step-select"><option value="">Aucun déplacement</option>{activeSteps.map((step) => <option key={step.id} value={step.id}>{step.name}</option>)}</select></label>
            </div>}
            <div className="automation-row">
              <p data-testid="automation-status-message">{settings.nocrm.automation_enabled ? "Automatisation webhook active." : "Déclencheur : Dossiers à enrichir. Sortie : Dossiers à confirmer."}</p>
              <button className="primary-button" type="button" onClick={activateAutomation} disabled={activatingAutomation || settings.nocrm.automation_enabled} data-testid="activate-automation-button">
                {activatingAutomation ? <LoaderCircle className="spinning" size={17} /> : <Zap size={17} />}
                {settings.nocrm.automation_enabled ? "Automatisation active" : "Activer l’automatisation"}
              </button>
            </div>
          </div>}
          {activityCatalogue && <div className="pipeline-controls" data-testid="activity-selector-panel">
            <label>Activité ajoutée au lead<select value={settings.nocrm.activity_id || ""} onChange={(event) => updateNocrm("activity_id", Number(event.target.value) || null)} data-testid="nocrm-activity-select"><option value="">Aucune activité</option>{activityCatalogue.activities.map((activity) => <option key={activity.id} value={activity.id}>{activity.name}</option>)}</select></label>
            <label>Auteur de l’activité<select value={settings.nocrm.activity_user_id || ""} onChange={(event) => updateNocrm("activity_user_id", Number(event.target.value) || null)} data-testid="nocrm-activity-user-select"><option value="">Responsable du lead (automatique)</option>{activityCatalogue.users.map((user) => <option key={user.id} value={user.id}>{`${user.firstname || ""} ${user.lastname || ""}`.trim() || user.email}</option>)}</select></label>
          </div>}
        </section>
        <AdminToolsSection />
      </div>
    </section>
  );
}